import { useEffect, useState, useCallback } from 'react';
import { IS_MOCK_DEMO } from '../../constants';

export default function AdminUserPanel({ user }) {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Invite Form State
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('credit_beta');
  const [inviteStatus, setInviteStatus] = useState('active');

  // Edit User State
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editStatus, setEditStatus] = useState('');

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      if (!user && !IS_MOCK_DEMO) return;

      if (IS_MOCK_DEMO) {
        setUsers([
          { uid: 'mock-user-1', email: 'demo@example.com', role: 'owner', status: 'active' },
          { uid: 'mock-user-2', email: 'user@example.com', role: 'credit_beta', status: 'active' },
          { uid: 'mock-user-3', email: 'byok@example.com', role: 'byok_user', status: 'active' },
        ]);
        setInvites([
          { email: 'invited@example.com', role: 'credit_beta', status: 'active' },
        ]);
        setLoading(false);
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const [usersResp, invitesResp] = await Promise.all([
          fetch('/api/v1/admin/users', { headers: { 'Authorization': `Bearer ${idToken}` } }),
          fetch('/api/v1/admin/invites', { headers: { 'Authorization': `Bearer ${idToken}` } })
        ]);

        if (!usersResp.ok || !invitesResp.ok) throw new Error('Failed to fetch data');

        const usersData = await usersResp.json();
        const invitesData = await invitesResp.json();

        if (isMounted) {
          setUsers(usersData.users || []);
          setInvites(invitesData.invites || []);
          setError(null);
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchData();
    return () => { isMounted = false; };
  }, [user, refreshTrigger]);

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteEmail || isProcessing) return;
    setIsProcessing(true);
    try {
      if (IS_MOCK_DEMO) {
        setInvites(prev => [...prev, { email: inviteEmail, role: inviteRole, status: inviteStatus }]);
        setInviteEmail('');
        return;
      }
      const idToken = await user.getIdToken();
      const resp = await fetch('/api/v1/admin/invites', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole, status: inviteStatus })
      });
      if (!resp.ok) throw new Error('Failed to create invitation');
      setInviteEmail('');
      handleRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteInvite = async (email) => {
    if (!window.confirm(`Delete invitation for ${email}?`)) return;
    setIsProcessing(true);
    try {
      if (IS_MOCK_DEMO) {
        setInvites(prev => prev.filter(inv => inv.email !== email));
        return;
      }
      const idToken = await user.getIdToken();
      const resp = await fetch(`/api/v1/admin/invites?email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (!resp.ok) throw new Error('Failed to delete invitation');
      handleRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const startEditing = (u) => {
    if (u.uid === user?.uid) return;
    setEditingUser(u);
    setEditRole(u.role || 'credit_beta');
    setEditStatus(u.status || 'active');
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditRole('');
    setEditStatus('');
  };

  const handleUpdateUser = async () => {
    if (!editingUser || isProcessing) return;
    if (editingUser.uid === user?.uid && (editRole !== 'owner' || editStatus === 'disabled')) {
      alert('自分自身の管理者権限はこの画面では変更できません。');
      return;
    }
    setIsProcessing(true);
    try {
      if (IS_MOCK_DEMO) {
        setUsers(prev => prev.map(u => u.uid === editingUser.uid ? { ...u, role: editRole, status: editStatus } : u));
        cancelEditing();
        return;
      }
      const idToken = await user.getIdToken();
      const resp = await fetch(`/api/v1/admin/users/${editingUser.uid}/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: editRole, status: editStatus })
      });
      if (!resp.ok) throw new Error('Failed to update user');
      cancelEditing();
      handleRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading && users.length === 0) return <div className="p-4 text-gray-400">Loading data...</div>;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-y-auto pb-10">
      {/* Invitation Form */}
      <div className="p-4 border-b border-gray-800">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">新規ユーザー招待</h3>
        <form onSubmit={handleInviteSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="メールアドレス"
            className="bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-xs"
            required
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-xs"
          >
            <option value="credit_beta">credit_beta</option>
            <option value="owner">owner</option>
            <option value="byok_user">byok_user</option>
          </select>
          <select
            value={inviteStatus}
            onChange={(e) => setInviteStatus(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-xs"
          >
            <option value="active">active</option>
            <option value="disabled">disabled</option>
          </select>
          <button
            type="submit"
            disabled={isProcessing}
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-4 rounded text-xs disabled:opacity-50"
          >
            {isProcessing ? '処理中...' : '招待を追加'}
          </button>
        </form>
      </div>

      {error && <div className="p-3 bg-rose-900/20 text-rose-400 text-xs border-b border-rose-900/30">Error: {error}</div>}

      {/* Invitations Table */}
      <div className="p-4">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2">招待済みリスト (trialInvites)</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full text-left text-[10px] border-collapse">
            <thead className="bg-gray-800 text-gray-400">
              <tr>
                <th className="p-2 border-b border-gray-700">Email</th>
                <th className="p-2 border-b border-gray-700">Role</th>
                <th className="p-2 border-b border-gray-700">Status</th>
                <th className="p-2 border-b border-gray-700 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {invites.map((inv) => (
                <tr key={inv.email} className="hover:bg-gray-800/40">
                  <td className="p-2 text-gray-300">{inv.email}</td>
                  <td className="p-2">
                    <span className="bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">{inv.role}</span>
                  </td>
                  <td className="p-2">
                    <span className={`px-1.5 py-0.5 rounded ${inv.status === 'active' ? 'bg-emerald-900/30 text-emerald-400' : 'bg-gray-700 text-gray-400'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="p-2 text-right">
                    <button
                      onClick={() => handleDeleteInvite(inv.email)}
                      className="text-rose-400 hover:text-rose-300 font-bold"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
              {invites.length === 0 && (
                <tr>
                  <td colSpan="4" className="p-4 text-center text-gray-500">招待されたユーザーはいません</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active Users Table */}
      <div className="p-4">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2">登録済みユーザー (users)</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full text-left text-[10px] border-collapse">
            <thead className="bg-gray-800 text-gray-400">
              <tr>
                <th className="p-2 border-b border-gray-700">Email / UID</th>
                <th className="p-2 border-b border-gray-700">Role</th>
                <th className="p-2 border-b border-gray-700">Status</th>
                <th className="p-2 border-b border-gray-700 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {users.map((u) => (
                <tr key={u.uid} className="hover:bg-gray-800/40">
                  <td className="p-2 text-gray-300">
                    <div>{u.email || 'No email'}{u.uid === user?.uid ? <span className="ml-2 text-[9px] text-gray-500">自分自身</span> : null}</div>
                    <div className="text-[8px] text-gray-600 font-mono">{u.uid}</div>
                  </td>
                  <td className="p-2">
                    {editingUser?.uid === u.uid ? (
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        className="bg-gray-700 border border-gray-600 text-white rounded px-1 py-0.5 text-[10px]"
                      >
                        <option value="owner">owner</option>
                        <option value="credit_beta">credit_beta</option>
                        <option value="byok_user">byok_user</option>
                      </select>
                    ) : (
                      <span className={`px-1.5 py-0.5 rounded font-bold ${u.role === 'owner' ? 'bg-rose-900/30 text-rose-400' : 'bg-blue-900/30 text-blue-400'}`}>
                        {u.role || 'member'}
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    {editingUser?.uid === u.uid ? (
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        className="bg-gray-700 border border-gray-600 text-white rounded px-1 py-0.5 text-[10px]"
                      >
                        <option value="active">active</option>
                        <option value="disabled">disabled</option>
                      </select>
                    ) : (
                      <span className={`px-1.5 py-0.5 rounded ${u.status === 'active' || !u.status ? 'bg-emerald-900/30 text-emerald-400' : 'bg-rose-900/30 text-rose-400'}`}>
                        {u.status || 'active'}
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {editingUser?.uid === u.uid ? (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={handleUpdateUser}
                          disabled={isProcessing}
                          className="text-emerald-400 hover:text-emerald-300 font-bold"
                        >
                          保存
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="text-gray-400 hover:text-gray-300"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditing(u)}
                        disabled={isProcessing || u.uid === user?.uid}
                        className={`font-bold ${u.uid === user?.uid ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300'}`}
                      >
                        編集
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
